<template>
  <div class="flex flex-row items-center">
    <CustomDropdown
      ref="dropdownRef"
      v-model="selectedPerPage"
      gap="10px"
      position="bottom"
    >
      <template #trigger>
        <div
          v-if="numberOfSelected === 0"
          class="flex items-center cursor-pointer"
        >
          <a-button class="primary_small text-sm">
            {{ triggerText }}
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW_BLACK"
              alt="bottom_arrow_grey"
              class="size-4"
            />
          </a-button>
        </div>
        <div v-else class="flex items-center gap-2 px-3 py-1 cursor-pointer">
          <span class="text-sm">{{ numberOfSelected }} selected</span>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
            class="size-4"
          />
        </div>
      </template>

      <template #default>
        <div class="dropdown_item" @click="handleSelectThisPage">
          Select this page
        </div>
        <div
          v-if="showSelectAll"
          class="dropdown_item"
          @click="handleSelectAll"
        >
          Select all ({{ totalCount }})
        </div>
        <template v-if="showSelectCertain">
          <div class="border_8 my-1"></div>
          <div class="dropdown_item select_certain_item">
            <span>Select certain number</span>
            <a-input
              v-model:value="selectNumberInput"
              class="input_medium w-[64px]"
              @keyup.enter="handleSelectCertain"
            />
            <a-button class="primary_medium px-2" @click="handleSelectCertain">
              <ThemeImage
                :icon="imageNames.CHECK_BLACK"
                alt="tick_icon"
                class="size-4"
              />
            </a-button>
          </div>
        </template>
        <div class="border_8 my-1"></div>
        <div class="dropdown_item" @click="handleUnselectAll">
          Unselect all
        </div>
      </template>
    </CustomDropdown>

    <div
      v-if="numberOfSelected > 0 && showUnselectButton"
      class="flex flex-row items-center gap-2"
    >
      <span
        class="text-sm secondary_color cursor-pointer mx-4"
        @click="handleUnselectAll"
      >
        Unselect
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { message } from "ant-design-vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  numberOfSelected: { type: Number, default: 0 },
  currentPageData: { type: Array, default: () => [] },
  allData: { type: Array, default: () => [] },
  totalCount: { type: Number, default: 0 },
  triggerText: { type: String, default: "Select items" },
  showSelectAll: { type: Boolean, default: true },
  showSelectCertain: { type: Boolean, default: true },
  showUnselectButton: { type: Boolean, default: true },
  idKey: { type: String, default: "id" },
  maxSelection: { type: Number, default: 0 },
});

const emit = defineEmits([
  "select-this-page",
  "select-all",
  "select-certain",
  "unselect-all",
]);

const selectedPerPage = ref(20);
const dropdownRef = ref(null);
const selectNumberInput = ref("");

const handleSelectThisPage = () => {
  const currentPageIds = props.currentPageData.map((item) => item[props.idKey]);
  emit("select-this-page", currentPageIds);
  dropdownRef.value?.closeDropdown?.();
};

const handleSelectAll = () => {
  if (props.maxSelection && props.totalCount > props.maxSelection) {
    message.warning(`Maximum selection limit is ${props.maxSelection}`);
    const selectedIds = props.allData
      ?.slice(0, props.maxSelection)
      .map((item) => item[props.idKey]);
    emit("select-certain", { number: props.maxSelection, selectedIds });
    dropdownRef.value?.closeDropdown?.();
    return;
  }
  if (props.allData && Array.isArray(props.allData)) {
    const allIds = props.allData.map((item) => item[props.idKey]);
    emit("select-all", allIds);
  } else {
    emit("select-all");
  }
  dropdownRef.value?.closeDropdown?.();
};

const handleSelectCertain = () => {
  const number = parseInt(selectNumberInput.value);
  if (props.maxSelection && number > props.maxSelection) {
    message.warning(`Maximum selection limit is ${props.maxSelection}`);
    selectNumberInput.value = "";
    dropdownRef.value?.closeDropdown?.();
    return;
  }
  if (number > 0) {
    const actualNumber = Math.min(number, props.totalCount);
    const selectedIds = props.allData
      .slice(0, actualNumber)
      .map((item) => item[props.idKey]);
    emit("select-certain", { number: actualNumber, selectedIds });
  }
  selectNumberInput.value = "";
  dropdownRef.value?.closeDropdown?.();
};

const handleUnselectAll = () => {
  emit("unselect-all");
  dropdownRef.value?.closeDropdown?.();
};
</script>

<style scoped>
.select_certain_item {
  cursor: default;
  padding-top: 4px;
  padding-bottom: 4px;
}

.select_certain_item:hover {
  background-color: transparent;
}
</style>
